// hooks/useValidation.ts
import { useCallback, useState } from 'react';
import type { InputNode } from '../types';
import { hasCompleteShopDateValue } from '../nodes/ShopDateSelectNode/utils';

const REQUIRES_FILE_TYPES = new Set(['upload']);

/** required 节点未填时的错误文案（batch upload / 单文件 upload / 其他）。 */
function messageOf(node: InputNode): string {
  if (REQUIRES_FILE_TYPES.has(node.type)) {
    const isBatch =
      typeof node.maxCount === 'number' &&
      typeof node.minCount === 'number' &&
      node.maxCount > 1;
    return isBatch ? `至少上传 ${node.minCount} 个文件` : '请上传';
  }
  return '请填写';
}

/** 错误 / 取值用的 key：module 节点用 composed `${key}__module_${idx}`（与 NodeRenderer 一致），否则 node.key。 */
function errorKeyOf(node: InputNode): string {
  return node.type === 'report-module' || node.type === 'markdown-module'
    ? `${node.key}__module_${node.moduleIndex ?? 0}`
    : (node.key as string);
}

/** 发送拦截 + 节点标红校验。语义对齐 V1 validateRequiredFields：
 *  ① 被互斥隐藏（excludedKeys，等价 V1 display:none）的节点跳过——看不见就不校验；
 *  ② 同一非 separator exclusionGroup 只需填一个即满足，整组空才对每个成员标红；
 *  ③ 无 group 的 required 各自必填。判定统一走 isNodeFilled（认 defaultValue / batch / composed key）。 */
export function validateRequired(
  config: InputNode[],
  values: Record<string, string | string[]>,
  files: Record<string, File | string | null>,
  excludedKeys: Set<string> = new Set(),
): Record<string, string> {
  const errors: Record<string, string> = {};
  const groups = new Map<string, InputNode[]>();

  for (const node of config) {
    if (!node.required || !node.key) continue;
    if (excludedKeys.has(node.key)) continue; // 被互斥隐藏（未渲染），用户填不了 → 不校验
    const g = node.exclusionGroup;
    if (g && g !== 'separator') {
      const arr = groups.get(g) ?? [];
      arr.push(node);
      groups.set(g, arr);
      continue;
    }
    // 无 group：各自必填
    if (!isNodeFilled(node, values, files)) errors[errorKeyOf(node)] = messageOf(node);
  }

  // 同一非 separator group：填一个即满足；整组都没填才对每个成员标红
  for (const [, nodes] of groups) {
    if (nodes.some((n) => isNodeFilled(n, values, files))) continue;
    for (const n of nodes) errors[errorKeyOf(n)] = messageOf(n);
  }
  return errors;
}

/** 判断单个 required 节点是否「已填」（computeFormValid 内部用）。
 *  upload 看 files（含 batch + minCount）；其他看 values[key] 非空，或 node.defaultValue 非空（如 select）。 */
function isNodeFilled(
  node: InputNode,
  values: Record<string, string | string[]>,
  files: Record<string, File | string | null>,
): boolean {
  if (!node.key) return true;
  if (node.type === 'shop-date-select') {
    return hasCompleteShopDateValue(values[node.key]);
  }
  if (REQUIRES_FILE_TYPES.has(node.type)) {
    const isBatch =
      typeof node.maxCount === 'number' &&
      typeof node.minCount === 'number' &&
      node.maxCount > 1;
    if (isBatch) {
      let found = 0;
      for (let i = 0; i < (node.maxCount ?? 0); i += 1) {
        if (files[`${node.key}-${i}`]) found += 1;
      }
      return found >= (node.minCount ?? 1);
    }
    return !!files[node.key];
  }
  const valueKey =
    node.type === 'report-module' || node.type === 'markdown-module'
      ? `${node.key}__module_${node.moduleIndex ?? 0}`
      : node.key;
  const v = values[valueKey];
  const hasValue =
    v !== undefined &&
    v !== '' &&
    !(Array.isArray(v) && (v.length === 0 || v.every((s) => !s || !s.trim())));
  if (hasValue) return true;
  // defaultValue（如 select 默认选项）视为已填——UI 已显示选中，未写回 values
  return node.defaultValue !== undefined && node.defaultValue !== null && String(node.defaultValue) !== '';
}

/** 发送按钮 isFormValid（对齐 V1 validateRequiredFields 的「互斥组只需填一个」语义）。
 *  与 validateRequired 不同点：① 同一 exclusionGroup 的多个 required 只需填其一；
 *  ② defaultValue 视为已填。仅用于发送按钮前置禁用态，不影响发送拦截/错误提示。 */
export function computeFormValid(
  config: InputNode[],
  values: Record<string, string | string[]>,
  files: Record<string, File | string | null>,
  excludedKeys: Set<string> = new Set(),
): boolean {
  // 被互斥隐藏的 required 节点不参与（与 validateRequired / 渲染层一致）
  const requiredNodes = config.filter(
    (n) => n.required && n.key && !excludedKeys.has(n.key),
  );
  if (requiredNodes.length === 0) return true;

  // 按 exclusionGroup 分组：有 group 的同组只需一个满足；无 group 的各自必填
  const groups = new Map<string, InputNode[]>();
  const ungrouped: InputNode[] = [];
  for (const node of requiredNodes) {
    const g = node.exclusionGroup;
    if (g && g !== 'separator') {
      const arr = groups.get(g) ?? [];
      arr.push(node);
      groups.set(g, arr);
    } else {
      ungrouped.push(node);
    }
  }

  for (const node of ungrouped) {
    if (!isNodeFilled(node, values, files)) return false;
  }
  for (const [, nodes] of groups) {
    if (!nodes.some((n) => isNodeFilled(n, values, files))) return false;
  }
  return true;
}

/**
 * required 的 markdown / markdown-name 不是“粘贴了文本”就算完成，必须由解析器
 * 产出至少一个模块。原始 config 用来保留父节点语义，excludedKeys 用来跳过互斥项。
 */
export function hasRequiredMarkdownParseResult(
  config: InputNode[],
  markdownModules: Readonly<Record<string, ReadonlyArray<unknown>>>,
  excludedKeys: ReadonlySet<string> = new Set(),
): boolean {
  return config.every((node) => {
    if (!node.required || !node.key || excludedKeys.has(node.key)) return true;
    if (node.type !== 'markdown' && node.type !== 'markdown-name') return true;
    return (markdownModules[node.key]?.length ?? 0) > 0;
  });
}

export function useValidation(config: InputNode[]) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const validate = useCallback(
    (
      values: Record<string, string | string[]>,
      files: Record<string, File | string | null>,
      excludedKeys: Set<string> = new Set(),
    ) => {
      const next = validateRequired(config, values, files, excludedKeys);
      setErrors(next);
      return Object.keys(next).length === 0;
    },
    [config],
  );
  const clearError = useCallback((key: string) => {
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);
  return { errors, validate, clearError };
}
